<script setup lang="ts">
import { ref } from "vue";
import type { InputProps } from "./input.types";
import { useInput } from "./useInput";
import { useConditionalSlots } from "composables/useConditionalSlots";
import InputTag from "./InputTag.vue";

defineOptions({ name: "EInput", inheritAttrs: false });

const props = withDefaults(defineProps<InputProps>(), {
  type: "text",
  autocomplete: "off",
  autofocusDelay: 0,
  color: "neutral",
  variant: "subtle",
  size: "md",
  block: false,
  square: false,
  multiple: false,
  duplicate: false,
  disabled: false,
  required: false,
});

const {
  finalColor,
  finalSize,
  classes,
  tagBindings,
  reorderContainerBindings,
  isMultiple,
  inputValue,
  inputRef,
  tags,
  removeTag,
  getDisplayValue,
  singleValue,
  isLeading,
  isTrailing,
  leadingIconName,
  trailingIconName,
  handleInput,
  handleKeydown,
  handleChange,
  handleBlur,
} = useInput(props);

const elRef = ref<HTMLElement | null>(null);

const { leading: hasLeading, trailing: hasTrailing } = useConditionalSlots(elRef, ["leading", "trailing"]);
</script>

<template>
  <e-primitive
    ref="elRef"
    as="div"
    exportparts="base,leading-icon,trailing-icon"
    :inner-class="classes.base"
    class="e-input"
    :data-disabled="disabled"
    :data-multiple="isMultiple"
    :data-square="square"
    v-bind="reorderContainerBindings()"
    @click="inputRef?.focus()"
  >
    <!-- Leading icon -->
    <span v-show="isLeading || hasLeading" part="leading" :class="classes.icon" class="e-input-icon e-input-leading">
      <slot name="leading">
        <e-icon v-if="isLeading && leadingIconName" exportparts="base:leading-icon" :name="leadingIconName" size="sm" />
      </slot>
    </span>

    <!-- Hidden inputs for form submission (multiple mode) -->
    <template v-if="isMultiple && name">
      <input
        v-for="(tag, index) in tags"
        :key="`hidden-${index}`"
        type="hidden"
        :name="name"
        :value="String(tag)"
        :disabled="disabled"
      />
    </template>

    <!-- Tags (multiple mode) -->
    <input-tag
      v-for="(tag, index) in tags"
      :key="String(tag)"
      v-bind="tagBindings(index)"
      class="e-input-tag"
      :value="tag"
      :display-value="getDisplayValue(tag)"
      :color="finalColor"
      :size="finalSize"
      :disabled="disabled"
      @remove="removeTag(tag)"
    />

    <!-- Input field -->
    <input
      :id="id"
      ref="inputRef"
      part="input"
      :class="classes.input"
      :type="type"
      :value="isMultiple ? inputValue : singleValue"
      :name="isMultiple ? undefined : name"
      :placeholder="placeholder"
      :disabled="disabled"
      :required="isMultiple ? required && tags.length === 0 : required"
      :autocomplete="autocomplete"
      v-bind="$attrs"
      @input="handleInput"
      @keydown="handleKeydown"
      @blur="handleBlur"
      @change="handleChange"
    />

    <slot />

    <!-- Trailing icon -->
    <span
      v-show="isTrailing || hasTrailing"
      part="trailing"
      :class="classes.icon"
      class="e-input-icon e-input-trailing"
    >
      <slot name="trailing">
        <e-icon
          v-if="isTrailing && trailingIconName"
          exportparts="base:trailing-icon"
          :name="trailingIconName"
          size="sm"
        />
      </slot>
    </span>
  </e-primitive>
</template>

<style>
:host {
  display: block;
}
</style>
